import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  BellRing,
  CheckCircle2,
  Clock3,
  Eye,
  Loader2,
  RefreshCw,
  Search,
  SlidersHorizontal,
  Smartphone,
  XCircle
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { alertAPI } from '../services/api';
import { getAlertMessage, getIotAlertLabel, normalizeAutogeneratedDeviceName } from '../utils/i18nFormatters';

const statusOptions = ['open', 'resolved'];
const alertTypeOptions = ['all', 'offline', 'maintenance', 'fault', 'low_quality'];
const severityOptions = ['all', 'low', 'medium', 'high', 'critical'];
const iotAlertCodes = [
  'noWater',
  'leakage',
  'MakeWaterLong',
  'counterErr',
  'FlowErr',
  'PumpHFErr',
  'lowMkErr',
  'veryLowMkErr',
  'UVErr',
  'BucketNoPress',
  'BucketLowPress',
  'noMakeWaterErr',
  'waterPosErr',
  'noInstallErr',
  'overHeat',
  'line_CM_err',
  'line_CL_err',
  'fireMore2HourErr',
  'waterPos1Err',
  'waterPos2Err'
];

const getQueryOption = (value, options, fallback = 'all') => (
  options.includes(value) ? value : fallback
);

export default function DeviceAlertsMonitor() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const unitIdFilter = searchParams.get('unitId') || '';
  const highlightedAlertId = searchParams.get('alertId') || '';
  const [alerts, setAlerts] = useState([]);
  const [stats, setStats] = useState(null);
  const [status, setStatus] = useState(() => {
    const queryStatus = searchParams.get('status');
    return statusOptions.includes(queryStatus) ? queryStatus : 'open';
  });
  const [alertType, setAlertType] = useState(() => (
    getQueryOption(searchParams.get('alertType'), alertTypeOptions)
  ));
  const [severity, setSeverity] = useState(() => (
    getQueryOption(searchParams.get('severity'), severityOptions)
  ));
  const [deviceQuery, setDeviceQuery] = useState(() => (
    searchParams.get('deviceQuery') || searchParams.get('deviceId') || ''
  ));
  const [faultType, setFaultType] = useState(() => searchParams.get('faultType') || '');
  const [expandedAlertId, setExpandedAlertId] = useState(null);
  const [deliveryStatusByAlert, setDeliveryStatusByAlert] = useState({});
  const [deliveryLoadingByAlert, setDeliveryLoadingByAlert] = useState({});
  const [deliveryErrorByAlert, setDeliveryErrorByAlert] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const loadRequestIdRef = useRef(0);

  const formatDateTime = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleString('en-PH', { timeZone: 'Asia/Manila' });
  };

  const normalizeNotificationRecipients = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [];
      } catch (_) {
        return [];
      }
    }
    return [];
  };

  const getRecipientGroups = (alert) => {
    const recipients = normalizeNotificationRecipients(alert.notificationRecipients);
    const groups = recipients.map((recipient) => recipient.roleGroup || recipient.role || 'operator');
    return [...new Set(groups)].filter(Boolean);
  };

  const loadData = useCallback(async () => {
    const requestId = loadRequestIdRef.current + 1;
    loadRequestIdRef.current = requestId;
    setLoading(true);
    setError('');
    try {
      const [alertsRes, statsRes] = await Promise.all([
        alertAPI.getAlerts({
          status,
          unitId: unitIdFilter || undefined,
          alertType: alertType !== 'all' ? alertType : undefined,
          severity: severity !== 'all' ? severity : undefined,
          deviceQuery: deviceQuery || undefined,
          faultType: faultType || undefined,
          limit: 100
        }),
        alertAPI.getStats()
      ]);
      if (requestId !== loadRequestIdRef.current) return;
      setAlerts(alertsRes.data?.alerts || []);
      setStats(statsRes.data?.stats || null);
    } catch (err) {
      if (requestId !== loadRequestIdRef.current) return;
      setError(err.response?.data?.message || err.message || t('deviceAlertsPage.errors.load'));
    } finally {
      if (requestId === loadRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, [alertType, deviceQuery, faultType, severity, status, t, unitIdFilter]);

  const loadDeliveryStatus = useCallback(async (alertId) => {
    if (!alertId || Object.prototype.hasOwnProperty.call(deliveryStatusByAlert, alertId)) return;
    setDeliveryLoadingByAlert((current) => ({ ...current, [alertId]: true }));
    setDeliveryErrorByAlert((current) => ({ ...current, [alertId]: '' }));
    try {
      const response = await alertAPI.getDeliveryStatus(alertId);
      setDeliveryStatusByAlert((current) => ({
        ...current,
        [alertId]: Array.isArray(response.data?.data) ? response.data.data : []
      }));
    } catch (loadError) {
      setDeliveryErrorByAlert((current) => ({
        ...current,
        [alertId]: loadError.response?.data?.message || loadError.message || t('deviceAlertsPage.notifications.deliveryLoadError')
      }));
    } finally {
      setDeliveryLoadingByAlert((current) => ({ ...current, [alertId]: false }));
    }
  }, [deliveryStatusByAlert, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const queryStatus = searchParams.get('status');
    if (statusOptions.includes(queryStatus) && queryStatus !== status) {
      setStatus(queryStatus);
    }

    const queryAlertType = getQueryOption(searchParams.get('alertType'), alertTypeOptions);
    if (queryAlertType !== alertType) {
      setAlertType(queryAlertType);
    }

    const querySeverity = getQueryOption(searchParams.get('severity'), severityOptions);
    if (querySeverity !== severity) {
      setSeverity(querySeverity);
    }

    const queryDevice = searchParams.get('deviceQuery') || searchParams.get('deviceId') || '';
    if (queryDevice !== deviceQuery) {
      setDeviceQuery(queryDevice);
    }

    const queryFault = searchParams.get('faultType') || '';
    if (queryFault !== faultType) {
      setFaultType(queryFault);
    }
  }, [alertType, deviceQuery, faultType, searchParams, severity, status]);

  useEffect(() => {
    if (highlightedAlertId) {
      setExpandedAlertId(Number(highlightedAlertId));
    }
  }, [highlightedAlertId]);

  useEffect(() => {
    if (expandedAlertId) loadDeliveryStatus(expandedAlertId);
  }, [expandedAlertId, loadDeliveryStatus]);

  const totals = useMemo(() => {
    const byStatus = Object.fromEntries((stats?.byStatus || []).map((item) => [item.status, Number(item.count)]));
    return {
      open: byStatus.open || 0,
      resolved: byStatus.resolved || 0
    };
  }, [stats]);

  const hasExtraFilters = Boolean(
    unitIdFilter ||
    deviceQuery ||
    faultType ||
    alertType !== 'all' ||
    severity !== 'all'
  );

  const getCurrentParams = () => new URLSearchParams(window.location.search || searchParams.toString());

  const setParamValue = (key, value) => {
    const nextParams = getCurrentParams();
    if (!value || value === 'all') {
      nextParams.delete(key);
    } else {
      nextParams.set(key, value);
    }
    nextParams.delete('alertId');
    setSearchParams(nextParams, { replace: true });
  };

  const handleStatusChange = (option) => {
    setStatus(option);
    const nextParams = getCurrentParams();
    nextParams.set('status', option);
    nextParams.delete('alertId');
    setSearchParams(nextParams, { replace: true });
  };

  const handleFilterChange = (key, option, setter) => {
    setter(option);
    setParamValue(key, option);
  };

  const clearAllFilters = () => {
    const nextParams = getCurrentParams();
    ['unitId', 'alertId', 'deviceId', 'deviceQuery', 'faultType', 'alertType', 'severity'].forEach((key) => {
      nextParams.delete(key);
    });
    setAlertType('all');
    setSeverity('all');
    setDeviceQuery('');
    setFaultType('');
    setSearchParams(nextParams, { replace: true });
  };

  const clearUnitFilter = () => {
    const nextParams = getCurrentParams();
    nextParams.delete('unitId');
    nextParams.delete('alertId');
    setSearchParams(nextParams, { replace: true });
  };

  const getDeviceLabel = (alert) => (
    alert.unit?.deviceId || t('deviceAlertsPage.unitFallback', { id: alert.unitId || '-' })
  );

  const isRoTdsTelemetryAlert = (alert) => alert.source === 'telemetry' && alert.sourceKey === 'ro_tds_over_50';

  const getAlertCodeLabel = (alert) => (
    isRoTdsTelemetryAlert(alert)
      ? t('deviceAlertsPage.telemetry.roTdsHigh', { defaultValue: 'RO TDS > 50' })
      : alert.errorCode || '-'
  );

  const getFaultLabel = (alert) => (
    isRoTdsTelemetryAlert(alert)
      ? t('deviceAlertsPage.telemetry.twoConsecutiveReadings', { defaultValue: 'Two consecutive high readings' })
      : getIotAlertLabel(t, alert.errorCode, alert.faultLabelZh || alert.errorCode || '-')
  );

  const getFrequencyText = (alert) => t('deviceAlertsPage.frequency.value', {
    count: Math.max(Number(alert.frequencyCount || 0), 1),
    days: Number(alert.frequencyWindowDays || 30)
  });

  const getDroppedAt = (alert) => alert.droppedAt || (alert.alertType === 'offline' ? alert.createdAt || alert.lastReportedAt : null);

  const renderSeverity = (severityValue) => (
    <span className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-black uppercase ${
      severityValue === 'critical' || severityValue === 'high'
        ? 'bg-red-50 text-[var(--admin-danger)] ring-1 ring-red-200'
        : severityValue === 'medium'
          ? 'bg-amber-50 text-[var(--admin-warning)] ring-1 ring-amber-200'
          : 'bg-cyan-50 text-[var(--admin-accent)] ring-1 ring-cyan-200'
    }`}>
      {t(`deviceAlertsPage.severity.${severityValue}`, { defaultValue: severityValue || '-' })}
    </span>
  );

  const renderNotificationInfo = (alert) => {
    const groups = getRecipientGroups(alert);
    return (
      <div className="space-y-2">
        <div className="text-xs font-black text-[var(--admin-text-primary)]">
          {t('deviceAlertsPage.notifications.count', { count: Number(alert.notificationCount || 0) })}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {groups.length ? groups.map((group) => (
            <span key={group} className="rounded-full bg-cyan-50 px-2 py-0.5 text-[11px] font-bold text-[var(--admin-accent)] ring-1 ring-cyan-100">
              {t(`deviceAlertsPage.notifications.roleGroups.${group}`, { defaultValue: group })}
            </span>
          )) : (
            <span className="text-xs text-[var(--admin-text-muted)]">{t('deviceAlertsPage.notifications.noRecipients')}</span>
          )}
        </div>
        <div className="text-[11px] leading-5 text-[var(--admin-text-muted)]">
          {t('deviceAlertsPage.notifications.nextReminder')}: {formatDateTime(alert.nextNotificationAt)}
        </div>
      </div>
    );
  };

  const renderRecipientGroupSummary = (alert) => {
    const groups = getRecipientGroups(alert);
    return groups.length
      ? groups.map((group) => t(`deviceAlertsPage.notifications.roleGroups.${group}`, { defaultValue: group })).join('、')
      : t('deviceAlertsPage.notifications.noRecipients');
  };

  const renderDeliveryStatus = (alert) => {
    const rows = deliveryStatusByAlert[alert.id] || [];
    const loadingStatus = deliveryLoadingByAlert[alert.id];
    const deliveryError = deliveryErrorByAlert[alert.id];

    return (
      <div className="mt-4 border-t border-[var(--admin-border-subtle)] pt-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="font-black text-[var(--admin-text-muted)]">{t('deviceAlertsPage.notifications.deliveryStatus')}</span>
          <span className="text-[11px] font-bold text-[var(--admin-text-muted)]">{t('deviceAlertsPage.notifications.deliveryPrivacy')}</span>
        </div>
        {loadingStatus ? (
          <div role="status" className="mt-3 flex items-center gap-2 text-xs font-bold text-[var(--admin-text-muted)]">
            <Loader2 size={15} className="animate-spin" />
            {t('deviceAlertsPage.notifications.deliveryLoading')}
          </div>
        ) : deliveryError ? (
          <div role="alert" className="mt-3 rounded-lg border border-red-200 bg-red-50 p-3 text-xs font-bold text-red-700">
            {deliveryError}
          </div>
        ) : rows.length === 0 ? (
          <p className="mt-3 text-xs text-[var(--admin-text-muted)]">{t('deviceAlertsPage.notifications.noDeliveryRecords')}</p>
        ) : (
          <div className="mt-3 space-y-2">
            {rows.map((recipient) => (
              <div key={recipient.notificationId} className="rounded-lg border border-[var(--admin-border-subtle)] bg-[var(--admin-surface)] p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-black text-[var(--admin-text-primary)]">
                    {t(`deviceAlertsPage.notifications.roleGroups.${recipient.recipientGroup}`, { defaultValue: recipient.recipientGroup || '-' })}
                  </span>
                  <span className="font-mono text-[11px] text-[var(--admin-text-muted)]">
                    {t('deviceAlertsPage.notifications.recipientReference', { id: recipient.recipientId || '-' })}
                  </span>
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {(recipient.deliveries || []).map((delivery) => (
                    <span
                      key={delivery.id}
                      className={`rounded-full px-2 py-1 text-[11px] font-black ring-1 ${
                        delivery.status === 'delivered' || delivery.status === 'sent'
                          ? 'bg-emerald-50 text-emerald-700 ring-emerald-200'
                          : delivery.status === 'failed'
                            ? 'bg-red-50 text-red-700 ring-red-200'
                            : delivery.status === 'pending'
                              ? 'bg-amber-50 text-amber-700 ring-amber-200'
                              : 'bg-slate-100 text-slate-700 ring-slate-200'
                      }`}
                    >
                      {t(`deviceAlertsPage.notifications.channels.${delivery.channel}`, { defaultValue: delivery.channel })}
                      {' · '}
                      {t(`deviceAlertsPage.notifications.deliveryStates.${delivery.status}`, { defaultValue: delivery.status })}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderResolutionInfo = (alert) => (
    <div className="space-y-1 text-sm">
      <div className="font-bold text-[var(--admin-text-primary)]">{formatDateTime(alert.resolvedAt)}</div>
      {alert.status === 'open' ? (
        <div className="inline-flex max-w-full items-center gap-1 rounded-full bg-blue-50 px-2 py-1 text-[11px] font-bold text-[var(--admin-primary)] ring-1 ring-blue-100">
          <Smartphone size={13} />
          <span className="break-words">{t('deviceAlertsPage.actions.appResolveOnly')}</span>
        </div>
      ) : (
        <div className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-1 text-[11px] font-bold text-[var(--admin-success)] ring-1 ring-emerald-100">
          <CheckCircle2 size={13} />
          {t('deviceAlertsPage.status.resolved')}
        </div>
      )}
    </div>
  );

  const renderAlertDetail = (alert, colSpan = 7) => (
    <tr className="border-t border-[var(--admin-border-subtle)] bg-[var(--admin-surface-muted)]">
      <td colSpan={colSpan} className="p-4 text-xs text-[var(--admin-text-secondary)]">
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          <div><span className="font-black text-[var(--admin-text-muted)]">{t('deviceAlertsPage.detail.deviceId')}</span><p className="mt-1 break-all font-mono text-[var(--admin-primary)]">{getDeviceLabel(alert)}</p></div>
          <div><span className="font-black text-[var(--admin-text-muted)]">{t('deviceAlertsPage.detail.deviceStatus')}</span><p className="mt-1">{alert.unit?.status || '-'}</p></div>
          <div><span className="font-black text-[var(--admin-text-muted)]">{t('deviceAlertsPage.detail.location')}</span><p className="mt-1 break-words">{alert.unit?.location || '-'}</p></div>
          <div><span className="font-black text-[var(--admin-text-muted)]">{t('deviceAlertsPage.detail.resolutionNotes')}</span><p className="mt-1 break-words">{alert.resolutionNotes || '-'}</p></div>
          <div><span className="font-black text-[var(--admin-text-muted)]">{t('deviceAlertsPage.detail.offlineSince')}</span><p className="mt-1">{formatDateTime(getDroppedAt(alert))}</p></div>
          <div><span className="font-black text-[var(--admin-text-muted)]">{t('deviceAlertsPage.detail.frequency')}</span><p className="mt-1">{getFrequencyText(alert)}</p></div>
          <div><span className="font-black text-[var(--admin-text-muted)]">{t('deviceAlertsPage.notifications.lastNotified')}</span><p className="mt-1">{formatDateTime(alert.lastNotifiedAt)}</p></div>
          <div><span className="font-black text-[var(--admin-text-muted)]">{t('deviceAlertsPage.notifications.lastError')}</span><p className="mt-1 break-words">{alert.notificationLastError || '-'}</p></div>
          <div className="md:col-span-2 xl:col-span-4">
            <span className="font-black text-[var(--admin-text-muted)]">{t('deviceAlertsPage.notifications.recipients')}</span>
            <p className="mt-1 break-words">{renderRecipientGroupSummary(alert)}</p>
          </div>
        </div>
        {renderDeliveryStatus(alert)}
      </td>
    </tr>
  );

  const renderMobileAlert = (alert) => (
    <article key={alert.id} className={`admin-card rounded-lg p-4 ${Number(highlightedAlertId) === Number(alert.id) ? 'ring-2 ring-amber-300' : ''}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1 text-sm font-black text-[var(--admin-text-primary)]">
              <AlertTriangle size={15} className="text-[var(--admin-warning)]" />
              {t(`deviceAlertsPage.types.${alert.alertType}`, { defaultValue: alert.alertType })}
            </span>
            {renderSeverity(alert.severity)}
          </div>
          <p className="mt-2 break-words text-sm leading-6 text-[var(--admin-text-secondary)]">{getAlertMessage(t, alert)}</p>
          <p className="mt-2 break-all font-mono text-xs text-[var(--admin-warning)]">{getAlertCodeLabel(alert)}</p>
          <p className="mt-1 break-words text-xs font-bold text-[var(--admin-text-muted)]">{getFaultLabel(alert)}</p>
        </div>
        <button
          onClick={() => setExpandedAlertId(expandedAlertId === alert.id ? null : alert.id)}
          className="admin-secondary-button inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg"
          title={expandedAlertId === alert.id ? t('deviceAlertsPage.actions.closeDetails') : t('deviceAlertsPage.actions.details')}
        >
          <Eye size={16} />
        </button>
      </div>
      <div className="mt-4 grid gap-3 text-sm sm:grid-cols-2">
        <div className="admin-muted-panel rounded-lg p-3">
          <span className="text-xs font-black text-[var(--admin-text-muted)]">{t('deviceAlertsPage.table.device')}</span>
          <Link to={`/units/${alert.unitId}`} className="mt-1 block break-all font-mono font-bold text-[var(--admin-primary)] underline-offset-4 hover:underline">
            {getDeviceLabel(alert)}
          </Link>
          <p className="mt-1 break-words text-xs text-[var(--admin-text-muted)]">{normalizeAutogeneratedDeviceName(alert.unit?.deviceName, alert.unit?.location || '-')}</p>
        </div>
        <div className="admin-muted-panel rounded-lg p-3">
          <span className="text-xs font-black text-[var(--admin-text-muted)]">{t('deviceAlertsPage.table.offlineFrequency')}</span>
          <p className="mt-1 font-bold text-[var(--admin-text-primary)]">{formatDateTime(getDroppedAt(alert))}</p>
          <p className="mt-1 text-xs text-[var(--admin-text-muted)]">{getFrequencyText(alert)}</p>
        </div>
        <div className="admin-muted-panel rounded-lg p-3">{renderNotificationInfo(alert)}</div>
        <div className="admin-muted-panel rounded-lg p-3">{renderResolutionInfo(alert)}</div>
      </div>
      {expandedAlertId === alert.id && (
        <div className="mt-4 rounded-lg border border-[var(--admin-border-subtle)] bg-[var(--admin-surface-muted)] p-3">
          <div className="grid gap-3 text-xs text-[var(--admin-text-secondary)]">
            <div><span className="font-black text-[var(--admin-text-muted)]">{t('deviceAlertsPage.detail.resolutionNotes')}</span><p className="mt-1 break-words">{alert.resolutionNotes || '-'}</p></div>
            <div><span className="font-black text-[var(--admin-text-muted)]">{t('deviceAlertsPage.notifications.recipients')}</span><p className="mt-1 break-words">{renderRecipientGroupSummary(alert)}</p></div>
            <div><span className="font-black text-[var(--admin-text-muted)]">{t('deviceAlertsPage.notifications.lastError')}</span><p className="mt-1 break-words">{alert.notificationLastError || '-'}</p></div>
          </div>
          {renderDeliveryStatus(alert)}
        </div>
      )}
    </article>
  );

  return (
    <div className="min-h-screen bg-[var(--admin-page-bg)] p-4 text-[var(--admin-text-primary)] sm:p-6 lg:p-8">
      <div className="mx-auto max-w-7xl space-y-6">
        <header className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="min-w-0">
            <h2 className="flex items-center gap-3 text-2xl font-black leading-9 text-[var(--admin-text-primary)] sm:text-3xl">
              <span className="admin-muted-panel flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-[var(--admin-primary)]">
                <BellRing size={24} />
              </span>
              <span className="break-words">{t('deviceAlertsPage.title')}</span>
            </h2>
            <p className="mt-2 max-w-3xl break-words text-sm leading-6 text-[var(--admin-text-muted)]">{t('deviceAlertsPage.subtitle')}</p>
          </div>
          <button onClick={loadData} className="admin-primary-button inline-flex h-11 items-center justify-center gap-2 rounded-lg px-4 text-sm font-black">
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            {t('common.refresh', { defaultValue: 'Refresh' })}
          </button>
        </header>

        {error && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-bold text-[var(--admin-danger)]">{error}</div>}
        {unitIdFilter && (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-[var(--admin-warning)]">
            <span className="break-words">
              {t('deviceAlertsPage.filters.unitActive', { defaultValue: 'Showing alerts for unit' })} <span className="font-mono font-black">#{unitIdFilter}</span>
            </span>
            <button onClick={clearUnitFilter} className="inline-flex items-center gap-1 rounded-lg border border-amber-300 px-3 py-1.5 text-xs font-black hover:bg-amber-100">
              <XCircle size={14} />
              {t('deviceAlertsPage.filters.clearUnit', { defaultValue: 'Clear unit filter' })}
            </button>
          </div>
        )}

        <section className="grid gap-4 md:grid-cols-2">
          <div className="admin-card rounded-lg border-l-4 border-l-amber-500 p-5">
            <div className="text-xs font-black uppercase text-[var(--admin-warning)]">{t('deviceAlertsPage.stats.open')}</div>
            <div className="mt-2 text-3xl font-black text-[var(--admin-text-primary)]">{totals.open}</div>
            <p className="mt-2 text-xs leading-5 text-[var(--admin-text-muted)]">{t('deviceAlertsPage.stats.openHint')}</p>
          </div>
          <div className="admin-card rounded-lg border-l-4 border-l-emerald-600 p-5">
            <div className="text-xs font-black uppercase text-[var(--admin-success)]">{t('deviceAlertsPage.stats.resolved')}</div>
            <div className="mt-2 text-3xl font-black text-[var(--admin-text-primary)]">{totals.resolved}</div>
            <p className="mt-2 text-xs leading-5 text-[var(--admin-text-muted)]">{t('deviceAlertsPage.stats.resolvedHint')}</p>
          </div>
        </section>

        <section className="admin-card rounded-lg p-4 sm:p-5">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
            <div className="min-w-0">
              <h3 className="flex items-center gap-2 text-lg font-black text-[var(--admin-text-primary)]">
                <SlidersHorizontal size={18} />
                {t('deviceAlertsPage.feed.title')}
              </h3>
              <p className="mt-1 max-w-2xl break-words text-sm leading-6 text-[var(--admin-text-muted)]">{t('deviceAlertsPage.feed.subtitle')}</p>
            </div>
            <div className="inline-flex w-full rounded-lg bg-[var(--admin-surface-soft)] p-1 sm:w-auto">
              {statusOptions.map((option) => (
                <button
                  key={option}
                  onClick={() => handleStatusChange(option)}
                  className={`h-10 flex-1 rounded-lg px-4 text-sm font-black transition sm:flex-none ${
                    status === option ? 'admin-primary-button' : 'text-[var(--admin-text-muted)] hover:text-[var(--admin-text-primary)]'
                  }`}
                >
                  {t(`deviceAlertsPage.status.${option}`)}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-5 grid gap-3 lg:grid-cols-[minmax(180px,1.2fr)_minmax(180px,1.2fr)_minmax(150px,0.8fr)_minmax(150px,0.8fr)_auto]">
            <label className="relative min-w-0">
              <span className="mb-1 block text-xs font-black text-[var(--admin-text-muted)]">{t('deviceAlertsPage.filters.deviceLabel')}</span>
              <Search size={16} className="pointer-events-none absolute bottom-3.5 left-3 text-[var(--admin-text-muted)]" />
              <input
                value={deviceQuery}
                onChange={(event) => handleFilterChange('deviceQuery', event.target.value, setDeviceQuery)}
                className="admin-input h-11 w-full rounded-lg py-2 pl-9 pr-3 text-sm outline-none"
                placeholder={t('deviceAlertsPage.filters.deviceSearch')}
              />
            </label>
            <label className="min-w-0">
              <span className="mb-1 block text-xs font-black text-[var(--admin-text-muted)]">{t('deviceAlertsPage.filters.faultLabel')}</span>
              <input
                list="iot-alert-code-options"
                value={faultType}
                onChange={(event) => handleFilterChange('faultType', event.target.value, setFaultType)}
                className="admin-input h-11 w-full rounded-lg px-3 py-2 text-sm outline-none"
                placeholder={t('deviceAlertsPage.filters.faultSearch')}
              />
              <datalist id="iot-alert-code-options">
                {iotAlertCodes.map((code) => (
                  <option key={code} value={code} label={getIotAlertLabel(t, code)} />
                ))}
              </datalist>
            </label>
            <label className="min-w-0">
              <span className="mb-1 block text-xs font-black text-[var(--admin-text-muted)]">{t('deviceAlertsPage.filters.alertTypeLabel')}</span>
              <select
                value={alertType}
                onChange={(event) => handleFilterChange('alertType', event.target.value, setAlertType)}
                className="admin-input h-11 w-full rounded-lg px-3 py-2 text-sm font-bold outline-none"
              >
                {alertTypeOptions.map((option) => (
                  <option key={option} value={option}>{option === 'all' ? t('deviceAlertsPage.filters.allTypes') : t(`deviceAlertsPage.types.${option}`, { defaultValue: option })}</option>
                ))}
              </select>
            </label>
            <label className="min-w-0">
              <span className="mb-1 block text-xs font-black text-[var(--admin-text-muted)]">{t('deviceAlertsPage.filters.severityLabel')}</span>
              <select
                value={severity}
                onChange={(event) => handleFilterChange('severity', event.target.value, setSeverity)}
                className="admin-input h-11 w-full rounded-lg px-3 py-2 text-sm font-bold outline-none"
              >
                {severityOptions.map((option) => (
                  <option key={option} value={option}>{option === 'all' ? t('deviceAlertsPage.filters.allSeverity') : t(`deviceAlertsPage.severity.${option}`, { defaultValue: option })}</option>
                ))}
              </select>
            </label>
            <div className="flex items-end">
              <button
                onClick={clearAllFilters}
                disabled={!hasExtraFilters}
                className="admin-secondary-button inline-flex h-11 w-full items-center justify-center gap-2 rounded-lg px-3 text-sm font-black disabled:cursor-not-allowed disabled:opacity-50 lg:w-auto"
              >
                <XCircle size={16} />
                {t('deviceAlertsPage.filters.clearAll')}
              </button>
            </div>
          </div>
        </section>

        <section className="admin-card hidden rounded-lg xl:block">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-[var(--admin-surface-soft)] text-[11px] font-black uppercase text-[var(--admin-text-muted)]">
                <tr>
                  <th className="p-4">{t('deviceAlertsPage.table.alert')}</th>
                  <th className="p-4">{t('deviceAlertsPage.table.device')}</th>
                  <th className="p-4">{t('deviceAlertsPage.table.severity')}</th>
                  <th className="p-4">{t('deviceAlertsPage.table.offlineFrequency')}</th>
                  <th className="p-4">{t('deviceAlertsPage.table.notifications')}</th>
                  <th className="p-4">{t('deviceAlertsPage.table.resolution')}</th>
                  <th className="p-4 text-right">{t('deviceAlertsPage.table.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {alerts.map((alert) => (
                  <React.Fragment key={alert.id}>
                    <tr className={`border-t border-[var(--admin-border-subtle)] hover:bg-[var(--admin-surface-muted)] ${Number(highlightedAlertId) === Number(alert.id) ? 'bg-amber-50' : ''}`}>
                      <td className="max-w-[320px] p-4 align-top">
                        <div className="flex items-center gap-2 font-black text-[var(--admin-text-primary)]">
                          <AlertTriangle size={15} className="text-[var(--admin-warning)]" />
                          {t(`deviceAlertsPage.types.${alert.alertType}`, { defaultValue: alert.alertType })}
                        </div>
                        <div className="mt-1 break-words text-sm leading-6 text-[var(--admin-text-secondary)]">{getAlertMessage(t, alert)}</div>
                        <div className="mt-2 break-all font-mono text-xs font-bold text-[var(--admin-warning)]">{getAlertCodeLabel(alert)}</div>
                        <div className="mt-1 break-words text-xs font-bold text-[var(--admin-text-muted)]">{getFaultLabel(alert)}</div>
                      </td>
                      <td className="max-w-[220px] p-4 align-top">
                        {alert.unitId ? (
                          <Link
                            to={`/units/${alert.unitId}`}
                            className="break-all font-mono font-black text-[var(--admin-primary)] underline-offset-4 transition-colors hover:underline"
                            title={t('deviceAlertsPage.actions.openUnit', { defaultValue: 'Open unit detail' })}
                          >
                            {getDeviceLabel(alert)}
                          </Link>
                        ) : (
                          <div className="break-all font-mono font-black text-[var(--admin-primary)]">{getDeviceLabel(alert)}</div>
                        )}
                        <div className="mt-1 break-words text-xs leading-5 text-[var(--admin-text-muted)]">{normalizeAutogeneratedDeviceName(alert.unit?.deviceName, alert.unit?.location || '-')}</div>
                      </td>
                      <td className="p-4 align-top">{renderSeverity(alert.severity)}</td>
                      <td className="max-w-[190px] p-4 align-top">
                        <div className="text-sm font-bold text-[var(--admin-text-primary)]">{formatDateTime(getDroppedAt(alert))}</div>
                        <div className="mt-1 text-xs leading-5 text-[var(--admin-text-muted)]">{getFrequencyText(alert)}</div>
                        <div className="mt-1 text-xs leading-5 text-[var(--admin-text-muted)]">{t('deviceAlertsPage.frequency.lastReported')}: {formatDateTime(alert.lastReportedAt)}</div>
                      </td>
                      <td className="max-w-[220px] p-4 align-top">{renderNotificationInfo(alert)}</td>
                      <td className="max-w-[220px] p-4 align-top">{renderResolutionInfo(alert)}</td>
                      <td className="p-4 align-top text-right">
                        <div className="flex flex-col items-end gap-2">
                          <button onClick={() => setExpandedAlertId(expandedAlertId === alert.id ? null : alert.id)} className="admin-secondary-button inline-flex items-center gap-1 rounded-lg px-3 py-2 text-xs font-black">
                            <Eye size={14} />
                            {expandedAlertId === alert.id ? t('deviceAlertsPage.actions.closeDetails') : t('deviceAlertsPage.actions.details')}
                          </button>
                          {alert.status !== 'open' && (
                            <span className="inline-flex items-center gap-1 text-xs font-bold text-[var(--admin-success)]">
                              <Clock3 size={14} />
                              {t(`deviceAlertsPage.status.${alert.status}`, { defaultValue: alert.status })}
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                    {expandedAlertId === alert.id && renderAlertDetail(alert)}
                  </React.Fragment>
                ))}
                {!alerts.length && !loading && (
                  <tr>
                    <td colSpan="7" className="p-10 text-center text-sm font-bold text-[var(--admin-text-muted)]">{t('deviceAlertsPage.empty')}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        <section className="space-y-4 xl:hidden">
          {alerts.map(renderMobileAlert)}
          {!alerts.length && !loading && (
            <div className="admin-card rounded-lg p-10 text-center text-sm font-bold text-[var(--admin-text-muted)]">{t('deviceAlertsPage.empty')}</div>
          )}
        </section>
      </div>
    </div>
  );
}
